import * as THREE from "three";
import RNG from "./rng.js";
import { Branch } from "./branch.js";
import { Billboard, TreeType } from "./enums.js";
import TreeOptions from "./options.js";
import { loadPreset } from "./presets/index.js";
import { Trellis } from "./trellis.js";

/** A raw attribute buffer, collected as plain number arrays during meshing then materialised into
 *  typed arrays in `#buildBufferGeometry`. */
export interface BufferGeometryData {
	verts: number[];
	normals: number[];
	indices: number[];
	uvs: number[];
	windFactor?: number[];
}

/** A skeleton branch: its per-section frames plus the meshing-parameter bookkeeping needed to emit
 *  the ring geometry. */
export interface SkeletonBranch {
	sections: {
		origin: THREE.Vector3;
		orientation: THREE.Euler;
		radius: number;
	}[];
	segmentCount: number;
	baseRadius: number;
}

/** A recorded leaf placement in the skeleton. Size variance is sampled here (during the RNG pass)
 *  so the meshing passes stay RNG-free. */
export interface SkeletonLeaf {
	origin: THREE.Vector3;
	orientation: THREE.Euler;
	size: number;
}

/** A material-free entry point's result: the two raw buffer-geometry meshes, unassigned to any
 *  material. */
export interface CreatedGeometry {
	branches: THREE.BufferGeometry;
	leaves: THREE.BufferGeometry;
}

/** Level-of-detail meshing detail controls. See `createGeometry`. */
export interface LODDetail {
	/** Sample every Nth section ring; the first and last rings are always kept so branch endpoints
	 *  stay put. */
	sectionStride?: number;
	/** Radial segment multiplier; segments = max(3, round(segmentCount * segmentFactor)). */
	segmentFactor?: number;
	/** Keep every Nth leaf. */
	leafStride?: number;
	/** Size multiplier for the kept leaves, typically 1/sqrt(kept fraction) to preserve canopy
	 *  coverage. */
	leafScale?: number;
	/** Billboard mode override for this level ('single' or 'double'); defaults to
	 *  options.leaves.billboard. */
	billboard?: Billboard;
}

/** A level descriptor for `generateLODs()`. */
export interface LODLevel {
	/** Camera distance at which this level activates. */
	distance: number;
	/** Switch hysteresis as a fraction of distance. */
	hysteresis?: number;
	/** Meshing detail for this level. */
	detail?: LODDetail;
}

export class Tree extends THREE.Group {
	/** The deterministic PRNG driving the skeleton pass. */
	rng!: RNG;
	/** The tree's parameters. */
	options: TreeOptions;
	/** Queued branches yet to be grown (drained during the skeleton pass). */
	branchQueue: Branch[] = [];
	/** The grown skeleton — branch section frames + leaf placements. */
	skeleton: { branches: SkeletonBranch[]; leaves: SkeletonLeaf[] } | null = null;

	branchesMesh = new THREE.Mesh();
	leavesMesh = new THREE.Mesh();
	trellisMesh: Trellis | null = null;
	lod: THREE.LOD | null = null;

	/** The raw branch geometry buffers (number arrays) from the most recent meshing pass. */
	branches!: BufferGeometryData;
	/** The raw leaf geometry buffers (number arrays) from the most recent meshing pass. */
	leaves!: BufferGeometryData;

	constructor(options: TreeOptions = new TreeOptions()) {
		super();
		this.name = "Tree";
		this.branchesMesh = new THREE.Mesh();
		this.leavesMesh = new THREE.Mesh();
		this.trellisMesh = null;
		this.lod = null;
		this.skeleton = null;
		this.add(this.branchesMesh);
		this.add(this.leavesMesh);
		this.options = options;
	}

	update(elapsedTime: number) {
		const leafShader = (this.leavesMesh.material as THREE.MeshStandardMaterial).userData.shader;
		if (leafShader) {
			leafShader.uniforms.uTime.value = elapsedTime;
		}
	}

	/**
	 * Loads a preset tree from JSON
	 * @param name The name of the preset to load
	 */
	loadPreset(name: string) {
		const json = loadPreset(name);
		this.loadFromJson(json);
	}

	/**
	 * Loads a tree from JSON
	 * @param json The tree options to load
	 */
	loadFromJson(json: TreeOptions) {
		this.options.copy(json);
		this.generate();
	}

	/**
	 * Default levels for generateLODs(). LOD1 is roughly 40% of the full triangle count, LOD2
	 * roughly 20%.
	 */
	static defaultLODLevels: LODLevel[] = [
		{ distance: 0, detail: {} },
		{
			distance: 100,
			hysteresis: 0.05,
			detail: {
				sectionStride: 3,
				segmentFactor: 0.75,
				leafStride: 2,
				// Slightly under the area-preserving sqrt(2): individual leaves are still resolvable
				// at this distance, so a full compensation reads as "bigger leaves" rather than
				// "same canopy".
				leafScale: 1.25,
			},
		},
		{
			distance: 250,
			hysteresis: 0.05,
			detail: {
				sectionStride: 6,
				segmentFactor: 0.4,
				leafStride: 2,
				// Deliberately under-compensated: full coverage compensation for the thinning +
				// single billboard would need 2x scale, which reads as balloon leaves. A slightly
				// sparser canopy with natural-size leaves looks better at this distance (fogged,
				// 250+ units in the demo).
				leafScale: 1.3,
				billboard: Billboard.Single,
			},
		},
	];

	/**
	 * Generate a new tree
	 */
	generate() {
		this.#clearLOD();
		this.#generateSkeleton();

		const buffers = this.#meshSkeleton();
		this.branches = buffers.branches;
		this.leaves = buffers.leaves;

		this.createBranchesGeometry();
		this.createLeavesGeometry();
		this.createTrellis();
	}

	/**
	 * Generates the tree as a set of levels of detail hosted in a THREE.LOD object inside this
	 * group. The renderer switches levels automatically based on camera distance. All levels share
	 * one bark and one leaf material, so update() animates wind at every level.
	 * @param levels Level descriptors, in any order
	 */
	generateLODs(levels: LODLevel[] = Tree.defaultLODLevels) {
		this.#clearLOD();
		this.#generateSkeleton();

		const barkMaterial = this.#createBarkMaterial();
		const leafMaterial = this.#createLeafMaterial();

		this.lod = new THREE.LOD();
		this.lod.name = "TreeLOD";
		const lod = this.lod;

		// THREE.LOD sorts its levels by distance internally, so sort here too and let the nearest
		// level own the reused meshes regardless of input order.
		const ordered = [...levels].sort(
			(a, b) => (a.distance ?? 0) - (b.distance ?? 0),
		);

		ordered.forEach((level, index) => {
			const buffers = this.#meshSkeleton(level.detail ?? {});

			let branchesMesh, leavesMesh;
			if (index === 0) {
				// Reuse the existing meshes for the closest level so update(), traversal and the
				// vertex/triangle count getters keep working.
				this.branches = buffers.branches;
				this.leaves = buffers.leaves;
				branchesMesh = this.branchesMesh;
				leavesMesh = this.leavesMesh;
				branchesMesh.geometry.dispose();
				(branchesMesh.material as THREE.Material).dispose();
				leavesMesh.geometry.dispose();
				(leavesMesh.material as THREE.Material).dispose();
			} else {
				branchesMesh = new THREE.Mesh();
				leavesMesh = new THREE.Mesh();
			}

			branchesMesh.geometry = this.#buildBufferGeometry(buffers.branches);
			branchesMesh.material = barkMaterial;
			leavesMesh.geometry = this.#buildBufferGeometry(buffers.leaves);
			leavesMesh.material = leafMaterial;

			for (const mesh of [branchesMesh, leavesMesh]) {
				mesh.castShadow = true;
				mesh.receiveShadow = true;
			}

			const group = new THREE.Group();
			group.add(branchesMesh, leavesMesh);
			lod.addLevel(group, level.distance ?? 0, level.hysteresis ?? 0);
		});

		this.add(lod);
		this.createTrellis();
	}

	/**
	 * Builds branch and leaf geometry at the given detail level without modifying the tree's own
	 * meshes. Useful for external instancing or custom LOD systems. Reuses the current skeleton,
	 * generating one first if none exists.
	 * @param detail Meshing detail for this pass
	 */
	createGeometry(detail: LODDetail = {}): CreatedGeometry {
		if (!this.skeleton) {
			this.#generateSkeleton();
		}
		const buffers = this.#meshSkeleton(detail);
		return {
			branches: this.#buildBufferGeometry(buffers.branches),
			leaves: this.#buildBufferGeometry(buffers.leaves),
		};
	}

	/**
	 * Tears down any LOD state and restores the flat branches/leaves meshes as direct children, so
	 * generate() behaves as if LODs never existed.
	 */
	#clearLOD() {
		if (!this.lod) return;

		this.lod.levels.forEach((level) => {
			for (const mesh of level.object.children) {
				// One level reuses branchesMesh/leavesMesh; their geometry and the shared materials
				// are disposed by whichever generate path runs next.
				if (mesh === this.branchesMesh || mesh === this.leavesMesh) continue;
				(mesh as THREE.Mesh).geometry.dispose();
			}
		});

		this.remove(this.lod);
		this.lod = null;
		this.add(this.branchesMesh, this.leavesMesh);
	}

	/**
	 * Grows the tree skeleton: the section frames of every branch and the placement of every leaf.
	 * All RNG consumption happens here, so any number of meshing passes can run against one
	 * skeleton without changing the tree's shape.
	 */
	#generateSkeleton() {
		this.skeleton = {
			branches: [],
			leaves: [],
		};

		this.rng = new RNG(this.options.seed);

		// Create the trunk of the tree first
		this.branchQueue.push(
			new Branch(
				new THREE.Vector3(),
				new THREE.Euler(),
				this.options.branch.length[0] ?? 20,
				this.options.branch.radius[0] ?? 1.5,
				0,
				this.options.branch.sections[0] ?? 12,
				this.options.branch.segments[0] ?? 8,
			),
		);

		while (this.branchQueue.length > 0) {
			const branch = this.branchQueue.shift()!;
			this.#growBranch(branch);
		}
	}

	/**
	 * Meshes the current skeleton into geometry buffers at the given detail. Consumes no RNG, so
	 * it can run repeatedly with different detail specs.
	 * @param detail Meshing detail for this pass
	 */
	#meshSkeleton(detail: LODDetail = {}): { branches: BufferGeometryData; leaves: BufferGeometryData } {
		const sectionStride = Math.max(1, Math.floor(detail.sectionStride ?? 1));
		const segmentFactor = detail.segmentFactor ?? 1;
		const leafStride = Math.max(1, Math.floor(detail.leafStride ?? 1));
		const leafScale = detail.leafScale ?? 1;
		const billboard = detail.billboard ?? this.options.leaves.billboard;

		const branches: BufferGeometryData = {
			verts: [],
			normals: [],
			indices: [],
			uvs: [],
			windFactor: [],
		};

		const leaves: BufferGeometryData = {
			verts: [],
			normals: [],
			indices: [],
			uvs: [],
		};

		for (const skeletonBranch of this.skeleton!.branches) {
			this.#meshBranch(branches, skeletonBranch, sectionStride, segmentFactor);
		}

		for (let i = 0; i < this.skeleton!.leaves.length; i += leafStride) {
			this.#meshLeaf(leaves, this.skeleton!.leaves[i], leafScale, billboard);
		}

		return { branches, leaves };
	}

	/**
	 * Grows a branch's skeleton, queueing child branches and recording leaf placements. Consumes
	 * RNG in the exact order of the original interleaved generator so seeds keep producing
	 * identical trees.
	 * @param branch The branch to grow
	 */
	#growBranch(branch: Branch) {
		let sectionOrientation = branch.orientation.clone();
		let sectionOrigin = branch.origin.clone();
		const sectionLength =
			branch.length /
			branch.sectionCount /
			// NOTE: preserved verbatim from upstream — this compares against the capital-D string
			// "Deciduous", which never matches the lowercase `TreeType.Deciduous` ("deciduous"), so
			// the ternary always yields 1. This is a latent upstream bug, but changing it would alter
			// the measured geometry (Set14 §2.1), so it is kept faithful.
			(String(this.options.type) === "Deciduous"
				? (this.options.branch.levels ?? 3) - 1
				: 1);

		// This information is used for generating child branches after the branch geometry has been
		// constructed
		let sections: {
			origin: THREE.Vector3;
			orientation: THREE.Euler;
			radius: number;
		}[] = [];

		for (let i = 0; i <= branch.sectionCount; i++) {
			let sectionRadius = branch.radius;

			// If final section of final level, set radius to effectively zero
			if (
				i === branch.sectionCount &&
				branch.level === this.options.branch.levels
			) {
				sectionRadius = 0.001;
			} else if (this.options.type === TreeType.Deciduous) {
				sectionRadius *=
					1 - (this.options.branch.taper[branch.level] ?? 0.7) * (i / branch.sectionCount);
			} else if (this.options.type === TreeType.Evergreen) {
				// Evergreens do not have a terminal branch so they have a taper of 1
				sectionRadius *= 1 - (i / branch.sectionCount);
			}

			// Use this information later on when generating child branches
			sections.push({
				origin: sectionOrigin.clone(),
				orientation: sectionOrientation.clone(),
				radius: sectionRadius,
			});

			sectionOrigin.add(
				new THREE.Vector3(0, sectionLength, 0).applyEuler(sectionOrientation),
			);

			// Perturb the orientation of the next section randomly. The higher the gnarliness, the
			// larger potential perturbation
			const gnarliness =
				Math.max(1, 1 / Math.sqrt(sectionRadius)) *
				(this.options.branch.gnarliness[branch.level] ?? 0);

			sectionOrientation.x += this.rng.random(gnarliness, -gnarliness);
			sectionOrientation.z += this.rng.random(gnarliness, -gnarliness);

			// Apply growth force to the branch
			const qSection = new THREE.Quaternion().setFromEuler(sectionOrientation);

			const qTwist = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(0, 1, 0),
				this.options.branch.twist[branch.level] ?? 0,
			);

			qSection.multiply(qTwist);

			// Rotate the section's growth direction toward force.direction (positive strength) or
			// away from it (negative). The (sectionUp × target) axis makes force.direction behave
			// as a real world axis: when sectionUp is already aligned with target the rotation is
			// zero, so a vertical trunk with force=(0,1,0) doesn't get gnarliness drift amplified —
			// the old slerp form was degenerate at qForce=identity and pushed branches in whatever
			// random direction the section had drifted.
			const sectionUp = new THREE.Vector3(0, 1, 0).applyQuaternion(qSection);
			const target = new THREE.Vector3()
				.copy(this.options.branch.force.direction)
				.normalize();
			const axis = new THREE.Vector3().crossVectors(sectionUp, target);
			const sinFull = axis.length();
			if (sinFull > 1e-6) {
				axis.divideScalar(sinFull);
				const fullAngle = Math.atan2(sinFull, sectionUp.dot(target));
				const step = this.options.branch.force.strength / sectionRadius;
				const clamped = Math.max(-fullAngle, Math.min(fullAngle, step));
				qSection.premultiply(
					new THREE.Quaternion().setFromAxisAngle(axis, clamped),
				);
			}

			// Apply trellis force if enabled
			if (this.options.trellis.enabled) {
				const trellisResult = this.calculateTrellisForce(sectionOrigin, sectionRadius);
				if (trellisResult) {
					const qTrellis = new THREE.Quaternion().setFromUnitVectors(
						new THREE.Vector3(0, 1, 0),
						trellisResult.direction,
					);
					qSection.rotateTowards(qTrellis, trellisResult.strength);
				}
			}

			sectionOrientation.setFromQuaternion(qSection);
		}

		this.skeleton!.branches.push({
			sections,
			segmentCount: branch.segmentCount,
			baseRadius: branch.radius,
		});

		// Deciduous trees have a terminal branch that grows out of the end of the parent branch
		if (this.options.type === TreeType.Deciduous) {
			const lastSection = sections[sections.length - 1];

			if (branch.level < (this.options.branch.levels ?? 3)) {
				this.branchQueue.push(
					new Branch(
						lastSection.origin,
						lastSection.orientation,
						this.options.branch.length[branch.level + 1] ?? 1,
						lastSection.radius,
						branch.level + 1,
						// Section count and segment count must be same as parent branch since the
						// child branch is growing from the end of the parent branch
						branch.sectionCount,
						branch.segmentCount,
					),
				);
			} else {
				this.#recordLeaf(lastSection.origin, lastSection.orientation);
			}
		}

		// If we are on the last branch level, generate leaves
		if (branch.level === this.options.branch.levels) {
			this.generateLeaves(sections);
		} else if (branch.level < (this.options.branch.levels ?? 3)) {
			this.generateChildBranches(
				this.options.branch.children[branch.level] ?? 7,
				branch.level + 1,
				sections);
		}
	}

	/**
	 * Generate branches from a parent branch
	 * @param count The number of child branches to generate
	 * @param level The level of the child branches
	 * @param sections The parent branch's sections
	 */
	generateChildBranches(
		count: number,
		level: number,
		sections: {
			origin: THREE.Vector3;
			orientation: THREE.Euler;
			radius: number;
		}[],
	) {
		const radialOffset = this.rng.random();
		const startMin = this.options.branch.start[level] ?? 0.3;
		const heightStep = (1.0 - startMin) / count;
		const angleSlots = this.shuffledIndices(count);

		for (let i = 0; i < count; i++) {
			// Stratified sampling along the parent's length: jitter within slot [i, i+1] so children
			// are spread evenly but not perfectly periodic.
			let childBranchStart = startMin + (i + this.rng.random()) * heightStep;

			// Find which sections are on either side of the child branch origin point so we can
			// determine the origin, orientation and radius of the branch
			const sectionIndex = Math.floor(childBranchStart * (sections.length - 1));
			let sectionA, sectionB;
			sectionA = sections[sectionIndex];
			if (sectionIndex === sections.length - 1) {
				sectionB = sectionA;
			} else {
				sectionB = sections[sectionIndex + 1];
			}

			// Find normalized distance from section A to section B (0 to 1)
			const alpha =
				(childBranchStart - sectionIndex / (sections.length - 1)) /
				(1 / (sections.length - 1));

			// Linearly interpolate origin from section A to section B
			const childBranchOrigin = new THREE.Vector3().lerpVectors(
				sectionA.origin,
				sectionB.origin,
				alpha,
			);

			// Linearly interpolate radius
			const childBranchRadius =
				(this.options.branch.radius[level] ?? 0.7) *
				((1 - alpha) * sectionA.radius + alpha * sectionB.radius);

			// Linearly interpolate the orientation
			const qA = new THREE.Quaternion().setFromEuler(sectionA.orientation);
			const qB = new THREE.Quaternion().setFromEuler(sectionB.orientation);
			const parentOrientation = new THREE.Euler().setFromQuaternion(
				qB.slerp(qA, alpha),
			);

			// Stratified radial angle: each child gets a 2π/count slot, jittered ±½ slot.
			// angleSlots[i] randomly permutes slot assignment so that the height slot and angle slot
			// are uncorrelated — otherwise evergreens (where branch length depends on height) spiral
			// their longest branches to a fixed side.
			const radialJitter = this.rng.random(0.5, -0.5);
			const radialAngle = 2.0 * Math.PI * (radialOffset + (angleSlots[i] + radialJitter) / count);
			const q1 = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(1, 0, 0),
				(this.options.branch.angle[level] ?? 60) / (180 / Math.PI),
			);
			const q2 = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(0, 1, 0),
				radialAngle,
			);
			const q3 = new THREE.Quaternion().setFromEuler(parentOrientation);

			const childBranchOrientation = new THREE.Euler().setFromQuaternion(
				q3.multiply(q2.multiply(q1)),
			);

			let childBranchLength =
				(this.options.branch.length[level] ?? 1) *
				(this.options.type === TreeType.Evergreen
					? 1.0 - childBranchStart
					: 1.0);

			this.branchQueue.push(
				new Branch(
					childBranchOrigin,
					childBranchOrientation,
					childBranchLength,
					childBranchRadius,
					level,
					this.options.branch.sections[level] ?? 8,
					this.options.branch.segments[level] ?? 4,
				),
			);
		}
	}

	/**
	 * Logic for spawning child branches from a parent branch's section
	 * @param sections The parent branch's sections
	 */
	generateLeaves(sections: {
		origin: THREE.Vector3;
		orientation: THREE.Euler;
		radius: number;
	}[]) {
		const radialOffset = this.rng.random();
		const count = this.options.leaves.count;
		const startMin = this.options.leaves.start;
		const heightStep = (1.0 - startMin) / count;
		const angleSlots = this.shuffledIndices(count);

		for (let i = 0; i < count; i++) {
			// Stratified sampling along the parent's length.
			let leafStart = startMin + (i + this.rng.random()) * heightStep;

			// Find which sections are on either side of the child branch origin point so we can
			// determine the origin, orientation and radius of the branch
			const sectionIndex = Math.floor(leafStart * (sections.length - 1));
			let sectionA, sectionB;
			sectionA = sections[sectionIndex];
			if (sectionIndex === sections.length - 1) {
				sectionB = sectionA;
			} else {
				sectionB = sections[sectionIndex + 1];
			}

			// Find normalized distance from section A to section B (0 to 1)
			const alpha =
				(leafStart - sectionIndex / (sections.length - 1)) /
				(1 / (sections.length - 1));

			// Linearly interpolate origin from section A to section B
			const leafOrigin = new THREE.Vector3().lerpVectors(
				sectionA.origin,
				sectionB.origin,
				alpha,
			);

			// Linearly interpolate the orientation
			const qA = new THREE.Quaternion().setFromEuler(sectionA.orientation);
			const qB = new THREE.Quaternion().setFromEuler(sectionB.orientation);
			const parentOrientation = new THREE.Euler().setFromQuaternion(
				qB.slerp(qA, alpha),
			);

			// Stratified radial angle with permuted slot assignment.
			// See generateChildBranches for rationale.
			const radialJitter = this.rng.random(0.5, -0.5);
			const radialAngle = 2.0 * Math.PI * (radialOffset + (angleSlots[i] + radialJitter) / count);
			const q1 = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(1, 0, 0),
				this.options.leaves.angle / (180 / Math.PI),
			);
			const q2 = new THREE.Quaternion().setFromAxisAngle(
				new THREE.Vector3(0, 1, 0),
				radialAngle,
			);
			const q3 = new THREE.Quaternion().setFromEuler(parentOrientation);

			const leafOrientation = new THREE.Euler().setFromQuaternion(
				q3.multiply(q2.multiply(q1)),
			);

			this.#recordLeaf(leafOrigin, leafOrientation);
		}
	}

	/**
	 * Records a leaf placement in the skeleton. The size variance is sampled here so the meshing
	 * passes stay RNG-free.
	 * @param origin The starting point of the leaf
	 * @param orientation The orientation of the leaf
	 */
	#recordLeaf(origin: THREE.Vector3, orientation: THREE.Euler) {
		const size =
			this.options.leaves.size *
			(1 +
				this.rng.random(
					this.options.leaves.sizeVariance,
					-this.options.leaves.sizeVariance,
				));

		this.skeleton!.leaves.push({
			origin: origin.clone(),
			orientation: orientation.clone(),
			size,
		});
	}

	/**
	 * Emits the quad geometry for one skeleton leaf into the buffers
	 * @param buffers The buffers to write into
	 * @param leaf The leaf to emit
	 * @param scale Size multiplier for this detail level
	 * @param billboard Billboard mode for this detail level
	 */
	#meshLeaf(buffers: BufferGeometryData, leaf: SkeletonLeaf, scale: number, billboard: Billboard) {
		let i = buffers.verts.length / 3;

		const { origin, orientation } = leaf;

		// Width and length of the leaf quad
		const leafSize = leaf.size * scale;

		const W = leafSize;
		const L = leafSize;

		const createLeaf = (rotation: number) => {
			// Create quad vertices
			const v = [
				new THREE.Vector3(-W / 2, L, 0),
				new THREE.Vector3(-W / 2, 0, 0),
				new THREE.Vector3(W / 2, 0, 0),
				new THREE.Vector3(W / 2, L, 0),
			].map((v) =>
				v
					.applyEuler(new THREE.Euler(0, rotation, 0))
					.applyEuler(orientation)
					.add(origin),
			);

			buffers.verts.push(
				v[0].x,
				v[0].y,
				v[0].z,
				v[1].x,
				v[1].y,
				v[1].z,
				v[2].x,
				v[2].y,
				v[2].z,
				v[3].x,
				v[3].y,
				v[3].z,
			);

			const n = new THREE.Vector3(0, 0, 1).applyEuler(orientation);

			// The normal vectors are an average of the direction of the leaf and the directions to
			// the individual vertices. This creates a nice rounded shape while maintaining the
			// canopy shape as a whole.
			const roundedNormals = this.options.leaves.roundedNormals;
			let n1 = roundedNormals ? new THREE.Vector3().copy(n).add(v[0]).sub(origin).normalize() : n;
			let n2 = roundedNormals ? new THREE.Vector3().copy(n).add(v[1]).sub(origin).normalize() : n;
			let n3 = roundedNormals ? new THREE.Vector3().copy(n).add(v[2]).sub(origin).normalize() : n;
			let n4 = roundedNormals ? new THREE.Vector3().copy(n).add(v[3]).sub(origin).normalize() : n;

			buffers.normals.push(
				n1.x,
				n1.y,
				n1.z,
				n2.x,
				n2.y,
				n2.z,
				n3.x,
				n3.y,
				n3.z,
				n4.x,
				n4.y,
				n4.z,
			);
			buffers.uvs.push(0, 1, 0, 0, 1, 0, 1, 1);
			buffers.indices.push(i, i + 1, i + 2, i, i + 2, i + 3);
			i += 4;
		};

		createLeaf(0);
		if (billboard === Billboard.Double) {
			createLeaf(Math.PI / 2);
		}
	}

	/**
	 * Fisher-Yates shuffle of [0..count-1] using the tree's RNG so results stay seed-reproducible.
	 * @param count The number of indices to shuffle
	 */
	shuffledIndices(count: number): number[] {
		const arr = Array.from({ length: count }, (_, k) => k);
		for (let k = count - 1; k > 0; k--) {
			const r = Math.floor(this.rng.random() * (k + 1));
			[arr[k], arr[r]] = [arr[r], arr[k]];
		}
		return arr;
	}

	/**
	 * Emits the ring geometry and indices for one skeleton branch
	 * @param buffers The buffers to write into
	 * @param skeletonBranch The skeleton branch to emit
	 * @param sectionStride Sample every Nth section ring
	 * @param segmentFactor Radial segment multiplier
	 */
	#meshBranch(
		buffers: BufferGeometryData,
		skeletonBranch: SkeletonBranch,
		sectionStride: number,
		segmentFactor: number,
	) {
		const { sections, segmentCount, baseRadius } = skeletonBranch;

		// Terminal branches inherit the parent's segmentCount, so parent and child resolve to the
		// same reduced count and junctions stay sealed.
		const segments = Math.max(3, Math.round(segmentCount * segmentFactor));

		// Number of texture wraps around the branch's circumference. Scaling with the branch's base
		// radius keeps bark feature size roughly consistent across thick trunks and thin twigs.
		// Held constant for the whole branch so tapered sections share a wrap count and don't twist
		// the texture longitudinally.
		const wrapsX = Math.max(
			1,
			Math.round(baseRadius * this.options.bark.textureScale.x),
		);

		// Sample every Nth ring, always keeping the first and last so branch endpoints (and
		// parent/child junctions) stay put across detail levels.
		const sampled: typeof sections = [];
		for (let i = 0; i < sections.length; i += sectionStride) {
			sampled.push(sections[i]);
		}
		if ((sections.length - 1) % sectionStride !== 0) {
			sampled.push(sections[sections.length - 1]);
		}

		// Used later for geometry index generation
		const indexOffset = buffers.verts.length / 3;

		for (let k = 0; k < sampled.length; k++) {
			const section = sampled[k];

			// Create the segments that make up this section.
			let first: { vertex: THREE.Vector3; normal: THREE.Vector3; uv: THREE.Vector2 } | undefined;
			for (let j = 0; j < segments; j++) {
				let angle = (2.0 * Math.PI * j) / segments;

				// Create the segment vertex
				const vertex = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
					.multiplyScalar(section.radius)
					.applyEuler(section.orientation)
					.add(section.origin);

				const normal = new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle))
					.applyEuler(section.orientation)
					.normalize();

				// uv.y alternates by sampled ring position rather than original section index, so
				// section skipping keeps the 0/1 tiling pattern.
				const uv = new THREE.Vector2(
					(j / segments) * wrapsX,
					(k % 2 === 0) ? 0 : 1,
				);

				buffers.verts.push(...Object.values(vertex));
				buffers.normals.push(...Object.values(normal));
				buffers.uvs.push(...Object.values(uv));

				if (j === 0) {
					first = { vertex, normal, uv };
				}
			}

			// Duplicate the first vertex so there is continuity in the UV mapping.
			// u=wrapsX maps to the same texel as u=0 since wrapsX is an integer.
			buffers.verts.push(...Object.values(first!.vertex));
			buffers.normals.push(...Object.values(first!.normal));
			buffers.uvs.push(wrapsX, first!.uv.y);
		}

		// Build geometry for each section of the branch (cylinder without end caps)
		let v1, v2, v3, v4;
		const N = segments + 1;
		for (let i = 0; i < sampled.length - 1; i++) {
			// Build the quad for each segment of the section
			for (let j = 0; j < segments; j++) {
				v1 = indexOffset + i * N + j;
				// The last segment wraps around back to the starting segment, so omit j + 1 term
				v2 = indexOffset + i * N + (j + 1);
				v3 = v1 + N;
				v4 = v2 + N;
				buffers.indices.push(v1, v3, v2, v2, v3, v4);
			}
		}
	}

	/**
	 * Builds a BufferGeometry from raw attribute buffers. Indices are Uint32 (never Uint16) —
	 * the upstream lib hard-coded Uint16Array, which silently wraps above 65 535 vertices (a
	 * foliage-heavy preset already measures 21 760 leaf verts; a denser one or a raised LOD
	 * crosses it and scrambles the geometry with no error).
	 * @param buffers The raw attribute buffers
	 */
	#buildBufferGeometry(buffers: BufferGeometryData): THREE.BufferGeometry {
		const g = new THREE.BufferGeometry();
		g.setAttribute(
			"position",
			new THREE.BufferAttribute(new Float32Array(buffers.verts), 3),
		);
		g.setAttribute(
			"normal",
			new THREE.BufferAttribute(new Float32Array(buffers.normals), 3),
		);
		g.setAttribute(
			"uv",
			new THREE.BufferAttribute(new Float32Array(buffers.uvs), 2),
		);
		g.setIndex(
			new THREE.BufferAttribute(new Uint32Array(buffers.indices), 1),
		);
		g.computeBoundingSphere();
		return g;
	}

	/**
	 * Creates the bark material from the current options
	 */
	#createBarkMaterial(): THREE.MeshStandardMaterial {
		const mat = new THREE.MeshStandardMaterial({
			name: "branches",
			flatShading: this.options.bark.flatShading,
			color: new THREE.Color(this.options.bark.tint),
			metalness: 0.0,
			roughness: 1.0,
		});

		if (this.options.bark.textured) {
			// textureScale.x is baked into UVs during meshing (wrapsX), so only the Y axis needs
			// runtime scaling on the texture itself.
			const scale = this.options.bark.textureScale;
			const maps = this.options.bark.maps;
			const apply = (texture: THREE.Texture | null): THREE.Texture | null => {
				if (!texture) return null;
				texture.wrapS = THREE.RepeatWrapping;
				texture.wrapT = THREE.RepeatWrapping;
				texture.repeat.x = 1;
				texture.repeat.y = 1 / scale.y;
				return texture;
			};
			if (maps.color) mat.map = apply(maps.color);
			if (maps.ao) mat.aoMap = apply(maps.ao);
			if (maps.normal) mat.normalMap = apply(maps.normal);
			if (maps.roughness) {
				mat.roughnessMap = apply(maps.roughness);
				// Point metalnessMap at the same texture: metalness stays 0 because the metalness
				// factor is 0, and GLTFExporter reuses the texture as-is instead of synthesizing a
				// merged metal/rough PNG (and warning about it) when the two slots differ.
				mat.metalnessMap = mat.roughnessMap;
			}
		}

		return mat;
	}

	/**
	 * Generates the geometry for the branches
	 */
	createBranchesGeometry() {
		this.branchesMesh.geometry.dispose();
		this.branchesMesh.geometry = this.#buildBufferGeometry(this.branches);
		(this.branchesMesh.material as THREE.Material).dispose();
		this.branchesMesh.material = this.#createBarkMaterial();
		this.branchesMesh.castShadow = true;
		this.branchesMesh.receiveShadow = true;
	}

	/**
	 * Creates the leaf material, including the wind sway vertex shader, from the current options
	 */
	#createLeafMaterial(): THREE.MeshStandardMaterial {
		const mat = new THREE.MeshStandardMaterial({
			name: "leaves",
			map: this.options.leaves.map ?? null,
			color: new THREE.Color(this.options.leaves.tint),
			side: THREE.DoubleSide,
			alphaTest: this.options.leaves.alphaTest,
			metalness: 0.0,
			roughness: 1.0,
			dithering: true
		});

		// Add custom shader code for branch swaying
		mat.onBeforeCompile = (shader) => {
			shader.uniforms.uTime = { value: 0 };
			shader.uniforms.uWindStrength = { value: new THREE.Vector3(0.5, 0, 0.5) };
			shader.uniforms.uWindFrequency = { value: 0.5 };
			shader.uniforms.uWindScale = { value: 70 };
			shader.uniforms.uCustomNormals = { value: this.options.leaves.roundedNormals };

			shader.vertexShader = `
				uniform float uTime;
				uniform vec3 uWindStrength;
				uniform float uWindFrequency;
				uniform float uWindScale;
				` + shader.vertexShader;

			// Add code for simplex noise
			shader.vertexShader = shader.vertexShader.replace(
				`void main() {`,
				`
				// GLSL Simplex Noise 3D
				// Source: https://github.com/ashima/webgl-noise

				vec3 mod289(vec3 x) {
					return x - floor(x * (1.0 / 289.0)) * 289.0;
				}

				vec4 mod289(vec4 x) {
					return x - floor(x * (1.0 / 289.0)) * 289.0;
				}

				vec4 permute(vec4 x) {
					return mod289(((x*34.0)+1.0)*x);
				}

				vec4 taylorInvSqrt(vec4 r) {
					return 1.79284291400159 - 0.85373472095314 * r;
				}

				vec3 fade(vec3 t) {
					return t*t*t*(t*(t*6.0-15.0)+10.0);
				}

				// Classic Simplex Noise 3D
				float simplex3(vec3 v) {
					const vec2  C = vec2(1.0/6.0, 1.0/3.0);
					const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

					// First corner
					vec3 i  = floor(v + dot(v, C.yyy) );
					vec3 x0 = v - i + dot(i, C.xxx);

					// Other corners
					vec3 g = step(x0.yzx, x0.xyz);
					vec3 l = 1.0 - g;
					vec3 i1 = min( g.xyz, l.zxy );
					vec3 i2 = max( g.xyz, l.zxy );

					//  x0 = x0 - 0. + 0.0 * C
					vec3 x1 = x0 - i1 + C.xxx;
					vec3 x2 = x0 - i2 + C.yyy; // 2.0 * C.x = 1/3 = C.y
					vec3 x3 = x0 - D.yyy;      // -1.0 + 3.0 * C.x = -0.5

					// Permutations
					i = mod289(i);
					vec4 p = permute( permute( permute(
								i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
							+ i.y + vec4(0.0, i1.y, i2.y, 1.0 ))
							+ i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

					// Gradients: 7x7 points over a square, mapped onto an octahedron.
					// The ring size 17*17 = 289 is close to the mapping's singularity.
					float n_ = 0.142857142857; // 1.0/7.0
					vec3  ns = n_ * D.wyz - D.xzx;

					vec4 j = p - 49.0 * floor(p * ns.z * ns.z);  //  mod(p,7*7)

					vec4 x_ = floor(j * ns.z);
					vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

					vec4 x = x_ *ns.x + ns.yyyy;
					vec4 y = y_ *ns.x + ns.yyyy;
					vec4 h = 1.0 - abs(x) - abs(y);

					vec4 b0 = vec4( x.xy, y.xy );
					vec4 b1 = vec4( x.zw, y.zw );

					vec4 s0 = floor(b0)*2.0 + 1.0;
					vec4 s1 = floor(b1)*2.0 + 1.0;
					vec4 sh = -step(h, vec4(0.0));

					vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
					vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

					vec3 g0 = vec3(a0.xy,h.x);
					vec3 g1 = vec3(a0.zw,h.y);
					vec3 g2 = vec3(a1.xy,h.z);
					vec3 g3 = vec3(a1.zw,h.w);

					// Normalise gradients
					vec4 norm = taylorInvSqrt(vec4(dot(g0,g0), dot(g1,g1), dot(g2,g2), dot(g3,g3)));
					g0 *= norm.x;
					g1 *= norm.y;
					g2 *= norm.z;
					g3 *= norm.w;

					// Mix contributions from the four corners
					vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
					m = m * m;
					return 42.0 * dot( m*m, vec4( dot(g0,x0), dot(g1,x1),
												dot(g2,x2), dot(g3,x3) ) );
				}

				void main() {`,
			);

			shader.vertexShader = shader.vertexShader.replace(
				`#include <project_vertex>`,
				`
				vec4 mvPosition = vec4(transformed, 1.0);

				float windOffset = 2.0 * 3.14 * simplex3(mvPosition.xyz / uWindScale);
				vec3 windSway = uv.y * uWindStrength * (
					0.5 * sin(uTime * uWindFrequency + windOffset) +
					0.3 * sin(2.0 * uTime * uWindFrequency + 1.3 * windOffset) +
					0.2 * sin(5.0 * uTime * uWindFrequency + 1.5 * windOffset)
				);
				mvPosition.xyz += windSway;

				mvPosition = modelViewMatrix * mvPosition;
				gl_Position = projectionMatrix * mvPosition;
				`
			);

			// Skip the backface normal flip in normal_fragment_begin when using custom normals
			shader.fragmentShader = `uniform bool uCustomNormals;\n` + shader.fragmentShader.replace(
				"#include <normal_fragment_begin>",
				THREE.ShaderChunk.normal_fragment_begin.replace(
					"normal *= faceDirection;",
					"if (!uCustomNormals) { normal *= faceDirection; }"
				)
			);

			// Non-enumerable so JSON serialization (e.g. GLTFExporter's userData pass) skips the
			// live shader object — Texture uniforms inside it are not serializable. update() still
			// reads userData.shader normally.
			Object.defineProperty(mat.userData, "shader", {
				value: shader,
				configurable: true,
				enumerable: false,
			});
		};

		return mat;
	}

	/**
	 * Generates the geometry for the leaves
	 */
	createLeavesGeometry() {
		this.leavesMesh.geometry.dispose();
		this.leavesMesh.geometry = this.#buildBufferGeometry(this.leaves);
		(this.leavesMesh.material as THREE.Material).dispose();
		this.leavesMesh.material = this.#createLeafMaterial();
		this.leavesMesh.castShadow = true;
		this.leavesMesh.receiveShadow = true;
	}

	/**
	 * Create or update the trellis geometry
	 */
	createTrellis() {
		// Remove old trellis if exists
		if (this.trellisMesh) {
			this.remove(this.trellisMesh);
			this.trellisMesh.dispose();
			this.trellisMesh = null;
		}

		// Create new trellis if enabled and visible
		if (this.options.trellis.enabled && this.options.trellis.visible) {
			this.trellisMesh = new Trellis(this.options.trellis);
			this.trellisMesh.generate();
			this.add(this.trellisMesh);
		}
	}

	/**
	 * Find the nearest point on the trellis grid to a given position
	 * @param position The position to project onto the trellis
	 */
	getNearestTrellisPoint(position: THREE.Vector3): THREE.Vector3 {
		const t = this.options.trellis;
		const trellisX = t.position.x;
		const trellisY = t.position.y;
		const trellisZ = t.position.z;

		// Trellis bounds
		const minX = trellisX - t.width / 2;
		const maxX = trellisX + t.width / 2;
		const minY = trellisY;
		const maxY = trellisY + t.height;

		// Clamp position to trellis bounds for projection
		const clampedX = Math.max(minX, Math.min(maxX, position.x));
		const clampedY = Math.max(minY, Math.min(maxY, position.y));

		// Find nearest horizontal line (Y = constant)
		const nearestHLineY = Math.round((clampedY - minY) / t.spacing) * t.spacing + minY;
		const finalHLineY = Math.max(minY, Math.min(maxY, nearestHLineY));

		// Find nearest vertical line (X = constant)
		const nearestVLineX = Math.round((clampedX - minX) / t.spacing) * t.spacing + minX;
		const finalVLineX = Math.max(minX, Math.min(maxX, nearestVLineX));

		// Point on nearest horizontal line (X can vary along the line)
		const pointOnHLine = new THREE.Vector3(clampedX, finalHLineY, trellisZ);

		// Point on nearest vertical line (Y can vary along the line)
		const pointOnVLine = new THREE.Vector3(finalVLineX, clampedY, trellisZ);

		// Return whichever is closer
		const distH = position.distanceTo(pointOnHLine);
		const distV = position.distanceTo(pointOnVLine);

		return distH < distV ? pointOnHLine : pointOnVLine;
	}

	/**
	 * Calculate the force vector toward the nearest trellis point
	 * @param position Current section position
	 * @param radius Current section radius
	 */
	calculateTrellisForce(
		position: THREE.Vector3,
		radius: number,
	): { direction: THREE.Vector3; strength: number } | null {
		const trellis = this.options.trellis;
		const nearestPoint = this.getNearestTrellisPoint(position);

		const distance = position.distanceTo(nearestPoint);

		// Only apply force within max distance
		if (distance > trellis.force.maxDistance) return null;
		if (distance < 0.001) return null; // Avoid division by zero

		// Calculate direction toward trellis
		const direction = new THREE.Vector3()
			.subVectors(nearestPoint, position)
			.normalize();

		// Calculate strength with distance falloff
		// Closer = stronger force, scaled by inverse radius (like existing force)
		const distanceFactor = 1 - Math.pow(
			distance / trellis.force.maxDistance,
			trellis.force.falloff,
		);
		const strength = trellis.force.strength * distanceFactor / radius;

		return { direction, strength };
	}

	get vertexCount(): number {
		return (this.branches.verts.length + this.leaves.verts.length) / 3;
	}

	get triangleCount(): number {
		return (this.branches.indices.length + this.leaves.indices.length) / 3;
	}
}
